import React, { useState, useEffect, Fragment, useMemo } from "react";
import {
  Dialog,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useUpdate from "../../../../api/useUpdate";
import { useNavigate } from "react-router-dom";

const EditCollectHtPatientFees = ({ open, onClose, payment, token }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state with advance currency
  const [formData, setFormData] = useState({
    ht_patient: null,
    amount: "",
    advance_amount: "",
    date: new Date().toISOString().split("T")[0],
    selected_currency: "",
    selected_advance_currency: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedHtPatient, setSelectedHtPatient] = useState(null);

  // Fetch the HT patients
  const { data: htPatients, loading: loadingHtPatients } = useFetchObjects(
    ["htPatients"],
    "ht-patients/?status_not=4",
    token
  );

  const { handleUpdate, loading: updating, success } = useUpdate(
    "ht-payments",
    token
  );

  // Map fetched patients for the Autocomplete
  const procedureData = useMemo(() => {
    if (!htPatients || !Array.isArray(htPatients)) return [];
    return htPatients.map((patient) => ({
      id: patient.id,
      label: `${patient.display_procedure_type} - ${
        patient.patient_name
      } - ${new Date(patient.procedure_date).toLocaleDateString()}`,
      total_cost: patient.total_cost,
      total_paid: patient.total_paid,
      balance_due: patient.balance_due,
      currency: patient.currency,
      advance: patient.advance,
      advance_currency: patient.advance_currency,
      exchange_rate: patient.exchange_rate,
      discount: patient.discount,
    }));
  }, [htPatients]);

  // When a payment is provided, prefill the form
  useEffect(() => {
    if (payment) {
      setFormData({
        ht_patient: payment.ht_patient || null,
        amount: payment.amount || "",
        advance_amount: payment.advance_amount || "",
        date: payment.date || new Date().toISOString().split("T")[0],
        selected_currency: payment.currency || "",
        selected_advance_currency:
          payment.advance_currency || payment.currency || "",
      });

      if (procedureData.length > 0 && payment.ht_patient) {
        const selected = procedureData.find(
          (item) => item.id === payment.ht_patient
        );
        if (selected) {
          setSelectedHtPatient(selected);
          setFormData((prev) => ({
            ...prev,
            selected_currency: selected.currency,
            selected_advance_currency:
              selected.advance_currency || selected.currency,
          }));
        }
      }
    }
  }, [payment, procedureData]);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success, onClose]);

  // Return a currency symbol
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 1:
        return "؋";
      case 2:
        return "$";
      case 3:
        return "€";
      default:
        return "";
    }
  };

  // Conversion logic - fixed to handle conversions correctly
  const computeConvertedValue = (
    value,
    fromCurrency,
    toCurrency,
    exchangeRate
  ) => {
    if (!value || isNaN(value)) return "";
    if (!fromCurrency || !toCurrency) return value;
    const originalCurrency = parseInt(fromCurrency);
    const chosenCurrency = parseInt(toCurrency);
    const rate = parseFloat(exchangeRate || 1);
    const num = parseFloat(value);
    if (originalCurrency === chosenCurrency) return num;
    if (
      (originalCurrency === 2 || originalCurrency === 3) &&
      chosenCurrency === 1
    ) {
      return num * rate; // Convert from USD/EUR to AFN: multiply by rate
    } else if (
      originalCurrency === 1 &&
      (chosenCurrency === 2 || chosenCurrency === 3)
    ) {
      return num / rate; // Convert from AFN to USD/EUR: divide by rate
    }
    return num;
  };

  // Round the conversion to two decimals
  const getFixedConversion = (
    value,
    fromCurrency,
    toCurrency,
    exchangeRate
  ) => {
    const converted = computeConvertedValue(
      value,
      fromCurrency,
      toCurrency,
      exchangeRate
    );
    return typeof converted === "number" ? Number(converted.toFixed(2)) : "";
  };

  // Handle procedure selection from the Autocomplete
  const handleProcedureSelect = (value) => {
    setSelectedHtPatient(value);
    setFormData((prev) => ({
      ...prev,
      ht_patient: value?.id || null,
      selected_currency: value?.currency || "",
      selected_advance_currency:
        value?.advance_currency || value?.currency || "",
    }));
    setFormErrors((prev) => ({ ...prev, ht_patient: "" }));
  };

  // Validate required fields
  const validateForm = () => {
    const errors = {};
    if (!formData.ht_patient)
      errors.ht_patient = t("reception.procedureRequired");
    if (!formData.date) errors.date = t("reception.dateRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to validate and format amount input
  const validateAndFormatAmount = (value) => {
    if (value === "") return value;

    // Remove any non-digit characters except decimal point
    const cleanedValue = value.replace(/[^0-9.]/g, "");

    // Split into integer and decimal parts
    const parts = cleanedValue.split(".");
    let integerPart = parts[0] || "";
    let decimalPart = parts[1] || "";

    // Ensure only one decimal point
    if (parts.length > 2) {
      decimalPart = parts.slice(1).join("");
    }

    // Limit integer part to 8 digits (to allow 2 decimal digits for total 10)
    if (integerPart.length > 8) {
      integerPart = integerPart.substring(0, 8);
    }

    // Limit decimal part to 2 digits
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }

    // Combine parts
    let formattedValue = integerPart;
    if (decimalPart) {
      formattedValue += `.${decimalPart}`;
    }

    return formattedValue;
  };

  const handleAmountChange = (e) => {
    const formattedValue = validateAndFormatAmount(e.target.value);
    setFormData({ ...formData, amount: formattedValue });
  };

  const handleAdvanceAmountChange = (e) => {
    const formattedValue = validateAndFormatAmount(e.target.value);
    setFormData({ ...formData, advance_amount: formattedValue });
  };

  // On form submission, apply conversion logic and call the update handler
  const handleSave = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    let dataToSave = {
      ht_patient: formData.ht_patient,
      date: formData.date,
      amount: formData.amount,
      advance_amount: formData.advance_amount,
    };

    // Convert main amount if currency is different
    if (selectedHtPatient && formData.selected_currency) {
      dataToSave.amount = computeConvertedValue(
        formData.amount,
        formData.selected_currency,
        selectedHtPatient.currency,
        selectedHtPatient.exchange_rate
      );
      dataToSave.currency = selectedHtPatient.currency;
    }

    // Convert advance amount if advance currency is different
    if (selectedHtPatient && formData.selected_advance_currency) {
      const targetAdvanceCurrency =
        selectedHtPatient.advance_currency || selectedHtPatient.currency;
      dataToSave.advance_amount = computeConvertedValue(
        formData.advance_amount,
        formData.selected_advance_currency,
        targetAdvanceCurrency,
        selectedHtPatient.exchange_rate
      );
      dataToSave.advance_currency = targetAdvanceCurrency;
    }

    // Ensure amounts are properly formatted before sending
    const formatForApi = (value) => {
      if (value === undefined || value === "") return "";
      const num = parseFloat(value);
      return num.toFixed(2);
    };

    if (dataToSave.amount !== undefined && dataToSave.amount !== "") {
      dataToSave.amount = formatForApi(dataToSave.amount);
    }
    if (
      dataToSave.advance_amount !== undefined &&
      dataToSave.advance_amount !== ""
    ) {
      dataToSave.advance_amount = formatForApi(dataToSave.advance_amount);
    }

    // Call the update handler
    handleUpdate(payment.id, dataToSave);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <Box
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          p: 2,
        }}
      >
        {/* Go Back Icon */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("reception.goBack")} arrow>
            <IconButton
              onClick={onClose}
              aria-label={t("reception.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
            gutterBottom
            sx={{ fontSize: isSmallScreen ? "1.8rem" : "2.5rem" }}
          >
            {t("reception.editHtPatientFees")}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {/* Payment Form Section */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                color="primary"
                mb={2}
                sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
              >
                {t("reception.paymentForm")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSave}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {loadingHtPatients ? (
                      <CircularProgress />
                    ) : (
                      <Autocomplete
                        options={procedureData}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => handleProcedureSelect(value)}
                        value={
                          procedureData.find(
                            (item) => item.id === formData.ht_patient
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("reception.selectProcedure")}
                            variant="outlined"
                            error={!!formErrors.ht_patient}
                            helperText={formErrors.ht_patient}
                            fullWidth
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.amount")}
                      variant="outlined"
                      fullWidth
                      type="text"
                      value={formData.amount}
                      onChange={handleAmountChange}
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "[0-9]*.?[0-9]{0,2}",
                      }}
                    />
                    {formData.amount &&
                      selectedHtPatient &&
                      formData.selected_currency && (
                        <Typography variant="caption" color="textSecondary">
                          {t("reception.afterExchange")}:{" "}
                          {getFixedConversion(
                            formData.amount,
                            formData.selected_currency,
                            selectedHtPatient.currency,
                            selectedHtPatient.exchange_rate
                          )}{" "}
                          {getCurrencySymbol(selectedHtPatient.currency)}
                        </Typography>
                      )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.date")}
                      variant="outlined"
                      fullWidth
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.advanceAmount")}
                      variant="outlined"
                      fullWidth
                      type="text"
                      value={formData.advance_amount}
                      onChange={handleAdvanceAmountChange}
                      error={!!formErrors.advance_amount}
                      helperText={formErrors.advance_amount}
                      disabled={
                        selectedHtPatient &&
                        selectedHtPatient.advance &&
                        parseFloat(formData.advance_amount) ===
                          parseFloat(selectedHtPatient.advance)
                      }
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "[0-9]*.?[0-9]{0,2}",
                      }}
                    />
                    {formData.advance_amount &&
                      selectedHtPatient &&
                      formData.selected_advance_currency && (
                        <Typography variant="caption" color="textSecondary">
                          {t("reception.afterExchange")}:{" "}
                          {getFixedConversion(
                            formData.advance_amount,
                            formData.selected_advance_currency,
                            selectedHtPatient.advance_currency ||
                              selectedHtPatient.currency,
                            selectedHtPatient.exchange_rate
                          )}{" "}
                          {getCurrencySymbol(
                            selectedHtPatient.advance_currency ||
                              selectedHtPatient.currency
                          )}
                        </Typography>
                      )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      component="fieldset"
                      error={!!formErrors.selected_currency}
                    >
                      <FormLabel component="legend">
                        {t("reception.selectCurrency")}
                      </FormLabel>
                      <RadioGroup
                        row
                        name="selected_currency"
                        value={formData.selected_currency}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selected_currency: parseInt(e.target.value),
                          })
                        }
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label={getCurrencySymbol(1)}
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label={getCurrencySymbol(2)}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label={getCurrencySymbol(3)}
                        />
                      </RadioGroup>
                      {formErrors.selected_currency && (
                        <Typography variant="caption" color="error">
                          {formErrors.selected_currency}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      component="fieldset"
                      error={!!formErrors.selected_advance_currency}
                    >
                      <FormLabel component="legend">
                        {t(
                          "reception.selectAdvanceCurrency",
                          "Select Advance Currency"
                        )}
                      </FormLabel>
                      <RadioGroup
                        row
                        name="selected_advance_currency"
                        value={formData.selected_advance_currency}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selected_advance_currency: parseInt(e.target.value),
                          })
                        }
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label={getCurrencySymbol(1)}
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label={getCurrencySymbol(2)}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label={getCurrencySymbol(3)}
                        />
                      </RadioGroup>
                      {formErrors.selected_advance_currency && (
                        <Typography variant="caption" color="error">
                          {formErrors.selected_advance_currency}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={updating}
                    sx={{
                      fontSize: isSmallScreen ? "0.9rem" : "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {updating ? (
                      <CircularProgress size={20} />
                    ) : (
                      t("reception.save")
                    )}
                  </Button>
                </Box>
              </form>
            </Card>
          </Grid>
          {/* Procedure Details Section */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 3,
                boxShadow: 3,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                  sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
                >
                  {t("reception.procedureDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {selectedHtPatient ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.patient")}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedHtPatient.label}
                      </Typography>
                    </Box>
                    <Grid container justifyContent={"space-between"}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("reception.totalCost")}
                        </Typography>
                        <Typography variant="h6">
                          {selectedHtPatient.total_cost}{" "}
                          {getCurrencySymbol(selectedHtPatient.currency)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("call_center.discount")}
                        </Typography>
                        <Typography variant="h6">
                          {selectedHtPatient.discount} %
                        </Typography>
                      </Box>
                      {selectedHtPatient.advance !== null && (
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {t("reception.advanceAmount")}
                          </Typography>
                          <Typography variant="h6">
                            {selectedHtPatient.advance}{" "}
                            {getCurrencySymbol(
                              selectedHtPatient.advance_currency ||
                                selectedHtPatient.currency
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.exchangeRate", "Exchange Rate")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedHtPatient.exchange_rate}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.totalPaid")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedHtPatient.total_paid}{" "}
                        {getCurrencySymbol(selectedHtPatient.currency)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.balanceDue")}
                      </Typography>
                      <Typography variant="h6" color="error">
                        {selectedHtPatient.balance_due}{" "}
                        {getCurrencySymbol(selectedHtPatient.currency)}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography textAlign="center" color="textSecondary">
                    {t("reception.noProcedureSelected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default EditCollectHtPatientFees;